import { useState, useEffect, useRef } from 'react'
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'
import VideoUrlFrame from './VideoUrlFrame'
import WebsiteUrlCard from './WebsiteUrlCard'

/**
 * HeroCarousel Component
 * Displays a professional auto-sliding carousel for hero section images
 * with smooth fade transitions and navigation controls
 */
export default function HeroCarousel({ 
  images, 
  settings, 
  heading, 
  headingPosition = 'left',
  subheading, 
  subheadingPosition = 'left',
  subheadingTextStyle = 'default',
  heroButtons, 
  overlay,
  ctaText,
  ctaLink,
  ctaBackgroundColor,
  ctaTextColor,
  displayMode = 'contain'
}) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const autoPlayIntervalRef = useRef(null)
  const viewportRef = useRef(null)

  // Validate inputs
  if (!images || images.length === 0) {
    return null
  }

  const carouselSettings = settings || {
    autoPlay: true,
    interval: 5000,
    transitionSpeed: 1000,
    showIndicators: true,
    showNavigation: true
  }

  // Auto-play functionality
  useEffect(() => {
    if (!carouselSettings.autoPlay || images.length <= 1 || isPaused) {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current)
      }
      return
    }

    autoPlayIntervalRef.current = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % images.length)
    }, carouselSettings.interval || 5000)

    return () => {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current)
      }
    }
  }, [images.length, carouselSettings.autoPlay, carouselSettings.interval, isPaused])

  const handlePrevious = () => {
    setCurrentIndex(prev => (prev - 1 + images.length) % images.length)
    setIsTransitioning(true)
    setTimeout(() => setIsTransitioning(false), carouselSettings.transitionSpeed || 1000)
  }

  const handleNext = () => {
    setCurrentIndex(prev => (prev + 1) % images.length)
    setIsTransitioning(true)
    setTimeout(() => setIsTransitioning(false), carouselSettings.transitionSpeed || 1000)
  }

  const handleDotClick = (index) => {
    setCurrentIndex(index)
    setIsTransitioning(true)
    setTimeout(() => setIsTransitioning(false), carouselSettings.transitionSpeed || 1000)
  }

  const currentImage = images[currentIndex]
  const transitionDuration = carouselSettings.transitionSpeed || 1000

  const renderMedia = (item, index) => {
    const type = item?.type || 'image'
    const mediaUrl = item?.url || item?.videoUrl || ''

    if (type === 'website') {
      return <WebsiteUrlCard url={mediaUrl} className="h-full min-h-[220px] rounded-none" />
    }

    if (type === 'video') {
      return <VideoUrlFrame video={item} className="h-full min-h-[220px] rounded-none" />
    }

    return mediaUrl ? (
      <img
        src={mediaUrl}
        alt={`Hero slide ${index + 1}`}
        className={displayMode === 'cover' ? 'block w-full h-full object-cover object-center' : 'block w-full h-auto object-contain object-center'}
        style={{ display: 'block', verticalAlign: 'top' }}
        loading={index === currentIndex ? 'eager' : 'lazy'}
        onError={(event) => {
          event.currentTarget.style.display = 'none'
          event.currentTarget.parentElement?.classList.add('bg-gray-100')
        }}
      />
    ) : (
      <div className="flex min-h-[220px] items-center justify-center bg-gray-100 text-sm text-gray-500">Media unavailable</div>
    )
  }

  const getHeroClickLink = () => {
    const cleanedCtaLink = ctaLink?.toString().trim()
    if (cleanedCtaLink) return cleanedCtaLink

    if (Array.isArray(heroButtons)) {
      const firstButton = heroButtons.find((btn) => btn?.link?.toString().trim())
      if (firstButton) return firstButton.link.toString().trim()
    }

    return ''
  }

  const heroClickLink = getHeroClickLink()

  const navigateToLink = (link) => {
    if (!link) return
    const trimmedLink = link.toString().trim()
    if (!trimmedLink) return

    if (trimmedLink.startsWith('#')) {
      window.location.hash = trimmedLink
      return
    }

    try {
      const url = new URL(trimmedLink, window.location.href)
      if (url.origin === window.location.origin) {
        window.location.href = url.href
      } else {
        window.open(url.href, '_blank', 'noopener,noreferrer')
      }
    } catch (error) {
      window.location.href = trimmedLink
    }
  }

  return (
    <section
      className={`relative w-screen left-1/2 -translate-x-1/2 max-w-screen overflow-hidden ${heroClickLink ? 'cursor-pointer' : ''}`}
      onMouseEnter={() => carouselSettings.autoPlay && setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onClick={(e) => {
        if (e.target.closest('button, iframe, video')) {
          e.stopPropagation()
          return
        }
        if (e.target.closest('[role="dialog"]')) {
          e.stopPropagation()
          return
        }
        navigateToLink(heroClickLink)
      }}
      role={heroClickLink ? 'link' : undefined}
      tabIndex={heroClickLink ? 0 : undefined}
      onKeyDown={(e) => {
        if (heroClickLink && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          navigateToLink(heroClickLink)
        }
      }}
    >
      {/* Carousel Container with Images - responsive contain-first layout */}
      <div
        ref={viewportRef}
        data-carousel-viewport="true"
        className="relative w-full overflow-hidden"
        style={{
          width: '100%',
          position: 'relative',
          maxWidth: '100%',
          lineHeight: 0,
          fontSize: 0
        }}
      >
        {images.map((img, idx) => {
          const isActive = idx === currentIndex

          return (
            <div
              key={img.id || idx}
              data-slide-index={idx}
              className="w-full transition-opacity"
              style={{
                display: isActive ? 'block' : 'none',
                opacity: isActive ? 1 : 0,
                transitionDuration: `${transitionDuration}ms`,
                transitionProperty: 'opacity',
                lineHeight: 0,
                verticalAlign: 'top',
                zIndex: isActive ? 1 : 0
              }}
            >
              {renderMedia(img, idx)}
            </div>
          )
        })}
      </div>

      {/* Recalculate height on resize or when active slide changes */}
      {/* The recomputeHeight function targets the currently visible slide's image and
          sets the viewportHeight so the image is shown whole (contain-first). */}
      {
        /* effects are executed in React hooks below */
      }

      {/* Hero Overlay */}
      {overlay?.enabled && (
        <div
          className="absolute inset-0 z-10"
          style={{
            backgroundColor: overlay.color || '#000000',
            opacity: overlay.opacity || 0.3
          }}
        />
      )}

      {/* Hero Content (Text and Buttons) */}
      <div className="absolute inset-0 z-20 flex items-center justify-center px-4 pointer-events-none">
        <div className="w-full max-w-3xl">
          {heading && (
            <h1 className="text-5xl md:text-6xl font-bold mb-4 drop-shadow-lg" style={{ textAlign: headingPosition }}>
              {heading}
            </h1>
          )}
          {subheading && (
            <p className={`text-xl md:text-2xl mb-8 drop-shadow-md ${subheadingTextStyle === 'bold' ? 'font-bold' : subheadingTextStyle === 'slightly-bold' ? 'font-medium' : ''}`} style={{ textAlign: subheadingPosition }}>
              {subheading}
            </p>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4 justify-center flex-wrap pointer-events-auto">
            {heroButtons && heroButtons.length > 0 && (
              heroButtons.map((btn) => (
                <a
                  key={btn.id}
                  href={btn.link || '#'}
                  onClick={(e) => e.stopPropagation()}
                  className={`px-4 sm:px-6 md:px-8 py-2 sm:py-2.5 md:py-3 rounded-lg font-semibold transition-all text-sm sm:text-base ${
                    btn.style === 'primary'
                      ? 'bg-white text-gray-900 hover:bg-gray-100'
                      : btn.style === 'secondary'
                      ? 'bg-gray-600 text-white hover:bg-gray-700'
                      : 'border-2 border-white text-white hover:bg-white hover:text-gray-900'
                  }`}
                >
                  {btn.text}
                </a>
              ))
            )}
            {ctaText && ctaLink && (
              <a
                href={ctaLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="px-4 sm:px-6 md:px-8 py-2 sm:py-2.5 md:py-3 rounded-lg font-semibold transition-all text-sm sm:text-base"
                style={{
                  backgroundColor: ctaBackgroundColor || '#ffffff',
                  color: ctaTextColor || '#111827'
                }}
              >
                {ctaText}
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      {carouselSettings.showNavigation !== false && images.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation()
              handlePrevious()
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-30 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all duration-200 group"
            aria-label="Previous slide"
            type="button"
          >
            <FiChevronLeft className="w-6 h-6 group-hover:scale-110 transition-transform" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation()
              handleNext()
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-30 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all duration-200 group"
            aria-label="Next slide"
            type="button"
          >
            <FiChevronRight className="w-6 h-6 group-hover:scale-110 transition-transform" />
          </button>
        </>
      )}

      {/* Indicator Dots */}
      {carouselSettings.showIndicators !== false && images.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex gap-2">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={(e) => {
                e.stopPropagation()
                handleDotClick(idx)
              }}
              className={`transition-all duration-300 rounded-full ${
                idx === currentIndex
                  ? 'bg-white w-3 h-3'
                  : 'bg-white/50 hover:bg-white/75 w-2.5 h-2.5'
              }`}
              aria-label={`Go to slide ${idx + 1}`}
              type="button"
            />
          ))}
        </div>
      )}

    </section>
  )
}











// import { useState, useEffect, useRef } from 'react'
// import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'

// /**
//  * HeroCarousel Component
//  * Displays a professional auto-sliding carousel for hero section images
//  * with smooth fade transitions and navigation controls
//  */
// export default function HeroCarousel({ 
//   images, 
//   settings, 
//   heading, 
//   subheading, 
//   heroButtons, 
//   overlay,
//   ctaText,
//   ctaLink,
//   ctaBackgroundColor,
//   ctaTextColor,
//   displayMode = 'contain'
// }) {
//   const [currentIndex, setCurrentIndex] = useState(0)
//   const [isTransitioning, setIsTransitioning] = useState(false)
//   const [isPaused, setIsPaused] = useState(false)
//   const autoPlayIntervalRef = useRef(null)
//   const viewportRef = useRef(null)
//   const [aspectRatio, setAspectRatio] = useState(9 / 16)

//   // Validate inputs
//   if (!images || images.length === 0) {
//     return null
//   }

//   const carouselSettings = settings || {
//     autoPlay: true,
//     interval: 5000,
//     transitionSpeed: 1000,
//     showIndicators: true,
//     showNavigation: true
//   }

//   // Auto-play functionality
//   useEffect(() => {
//     if (!carouselSettings.autoPlay || images.length <= 1 || isPaused) {
//       if (autoPlayIntervalRef.current) {
//         clearInterval(autoPlayIntervalRef.current)
//       }
//       return
//     }

//     autoPlayIntervalRef.current = setInterval(() => {
//       setCurrentIndex(prev => (prev + 1) % images.length)
//     }, carouselSettings.interval || 5000)

//     return () => {
//       if (autoPlayIntervalRef.current) {
//         clearInterval(autoPlayIntervalRef.current)
//       }
//     }
//   }, [images.length, carouselSettings.autoPlay, carouselSettings.interval, isPaused])

//   const handlePrevious = () => {
//     setCurrentIndex(prev => (prev - 1 + images.length) % images.length)
//     setIsTransitioning(true)
//     setTimeout(() => setIsTransitioning(false), carouselSettings.transitionSpeed || 1000)
//   }

//   const handleNext = () => {
//     setCurrentIndex(prev => (prev + 1) % images.length)
//     setIsTransitioning(true)
//     setTimeout(() => setIsTransitioning(false), carouselSettings.transitionSpeed || 1000)
//   }

//   const handleDotClick = (index) => {
//     setCurrentIndex(index)
//     setIsTransitioning(true)
//     setTimeout(() => setIsTransitioning(false), carouselSettings.transitionSpeed || 1000)
//   }

//   const currentImage = images[currentIndex]
//   const transitionDuration = carouselSettings.transitionSpeed || 1000

//   const getHeroClickLink = () => {
//     const cleanedCtaLink = ctaLink?.toString().trim()
//     if (cleanedCtaLink) return cleanedCtaLink

//     if (Array.isArray(heroButtons)) {
//       const firstButton = heroButtons.find((btn) => btn?.link?.toString().trim())
//       if (firstButton) return firstButton.link.toString().trim()
//     }

//     return ''
//   }

//   const heroClickLink = getHeroClickLink()

//   const navigateToLink = (link) => {
//     if (!link) return
//     const trimmedLink = link.toString().trim()
//     if (!trimmedLink) return

//     if (trimmedLink.startsWith('#')) {
//       window.location.hash = trimmedLink
//       return
//     }

//     try {
//       const url = new URL(trimmedLink, window.location.href)
//       if (url.origin === window.location.origin) {
//         window.location.href = url.href
//       } else {
//         window.open(url.href, '_blank', 'noopener,noreferrer')
//       }
//     } catch (error) {
//       window.location.href = trimmedLink
//     }
//   }

//   // Recompute viewport height to preserve contain-first rendering
//   const handleImageLoad = (e) => {
//     try {
//       const naturalW = e.target.naturalWidth || 0
//       const naturalH = e.target.naturalHeight || 0
//       if (naturalW && naturalH) {
//         setAspectRatio(naturalH / naturalW)
//       }
//     } catch (err) {
//       // ignore
//     }
//   }

//   return (
//     <section
//       className={`relative w-full flex items-center justify-center ${heroClickLink ? 'cursor-pointer' : ''}`}
//       onMouseEnter={() => carouselSettings.autoPlay && setIsPaused(true)}
//       onMouseLeave={() => setIsPaused(false)}
//       onClick={(e) => {
//         if (e.target.closest('[role="dialog"]')) {
//           e.stopPropagation()
//           return
//         }
//         navigateToLink(heroClickLink)
//       }}
//       role={heroClickLink ? 'link' : undefined}
//       tabIndex={heroClickLink ? 0 : undefined}
//       onKeyDown={(e) => {
//         if (heroClickLink && (e.key === 'Enter' || e.key === ' ')) {
//           e.preventDefault()
//           navigateToLink(heroClickLink)
//         }
//       }}
//     >
//       {/* Carousel Container with Images - responsive contain-first layout */}
//       <div
//         ref={viewportRef}
//         className="relative w-full overflow-hidden"
//         style={{
//           width: '100%',
//           aspectRatio: displayMode === 'cover' ? undefined : (aspectRatio || (16 / 9)),
//           minHeight: '40vh',
//           maxHeight: displayMode === 'cover' ? '70vh' : '90vh'
//         }}
//       >
//         {images.map((img, idx) => (
//           <div
//             key={img.id || idx}
//             data-slide-index={idx}
//             className="absolute inset-0 w-full h-full transition-opacity"
//             style={{
//               opacity: idx === currentIndex ? 1 : 0,
//               transitionDuration: `${transitionDuration}ms`,
//               transitionProperty: 'opacity',
//               zIndex: idx === currentIndex ? 1 : 0
//             }}
//           >
//             {img.url && (
//               displayMode === 'cover' ? (
//                 <img
//                   src={img.url}
//                   alt={`Hero slide ${idx + 1}`}
//                   className="absolute inset-0 w-full h-full object-cover"
//                   loading={idx === currentIndex ? 'eager' : 'lazy'}
//                   onError={(e) => {
//                     console.error(`[HeroCarousel] Image ${idx + 1} failed to load:`, img.url?.substring(0, 100) + '...')
//                   }}
//                 />
//               ) : (
//                 <img
//                   src={img.url}
//                   alt={`Hero slide ${idx + 1}`}
//                   className="absolute inset-0 w-full h-full object-contain"
//                   style={{ objectFit: 'contain' }}
//                   loading={idx === currentIndex ? 'eager' : 'lazy'}
//                   onLoad={handleImageLoad}
//                   onError={(e) => {
//                     console.error(`[HeroCarousel] Image ${idx + 1} failed to load:`, img.url?.substring(0, 100) + '...')
//                   }}
//                 />
//               )
//             )}
//           </div>
//         ))}
//       </div>

//       {/* Recalculate height on resize or when active slide changes */}
//       {/* The recomputeHeight function targets the currently visible slide's image and
//           sets the viewportHeight so the image is shown whole (contain-first). */}
//       {
//         /* effects are executed in React hooks below */
//       }

//       {/* Hero Overlay */}
//       {overlay?.enabled && (
//         <div
//           className="absolute inset-0 z-10"
//           style={{
//             backgroundColor: overlay.color || '#000000',
//             opacity: overlay.opacity || 0.3
//           }}
//         />
//       )}

//       {/* Hero Content (Text and Buttons) */}
//       <div className="relative z-20 text-center text-white px-4 max-w-3xl pointer-events-none">
//         {heading && (
//           <h1 className="text-5xl md:text-6xl font-bold mb-4 drop-shadow-lg">
//             {heading}
//           </h1>
//         )}
//         {subheading && (
//           <p className="text-xl md:text-2xl mb-8 drop-shadow-md">
//             {subheading}
//           </p>
//         )}

//         {/* Action Buttons */}
//         <div className="flex gap-4 justify-center flex-wrap pointer-events-auto">
//           {heroButtons && heroButtons.length > 0 && (
//             heroButtons.map((btn) => (
//               <a
//                 key={btn.id}
//                 href={btn.link || '#'}
//                 onClick={(e) => e.stopPropagation()}
//                 className={`px-4 sm:px-6 md:px-8 py-2 sm:py-2.5 md:py-3 rounded-lg font-semibold transition-all text-sm sm:text-base ${
//                   btn.style === 'primary'
//                     ? 'bg-white text-gray-900 hover:bg-gray-100'
//                     : btn.style === 'secondary'
//                     ? 'bg-gray-600 text-white hover:bg-gray-700'
//                     : 'border-2 border-white text-white hover:bg-white hover:text-gray-900'
//                 }`}
//               >
//                 {btn.text}
//               </a>
//             ))
//           )}
//           {ctaText && ctaLink && (
//             <a
//               href={ctaLink}
//               target="_blank"
//               rel="noopener noreferrer"
//               onClick={(e) => e.stopPropagation()}
//               className="px-4 sm:px-6 md:px-8 py-2 sm:py-2.5 md:py-3 rounded-lg font-semibold transition-all text-sm sm:text-base"
//               style={{
//                 backgroundColor: ctaBackgroundColor || '#ffffff',
//                 color: ctaTextColor || '#111827'
//               }}
//             >
//               {ctaText}
//             </a>
//           )}
//         </div>
//       </div>

//       {/* Navigation Arrows */}
//       {carouselSettings.showNavigation !== false && images.length > 1 && (
//         <>
//           <button
//             onClick={(e) => {
//               e.stopPropagation()
//               handlePrevious()
//             }}
//             className="absolute left-4 top-1/2 -translate-y-1/2 z-30 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all duration-200 group"
//             aria-label="Previous slide"
//             type="button"
//           >
//             <FiChevronLeft className="w-6 h-6 group-hover:scale-110 transition-transform" />
//           </button>

//           <button
//             onClick={(e) => {
//               e.stopPropagation()
//               handleNext()
//             }}
//             className="absolute right-4 top-1/2 -translate-y-1/2 z-30 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all duration-200 group"
//             aria-label="Next slide"
//             type="button"
//           >
//             <FiChevronRight className="w-6 h-6 group-hover:scale-110 transition-transform" />
//           </button>
//         </>
//       )}

//       {/* Indicator Dots */}
//       {carouselSettings.showIndicators !== false && images.length > 1 && (
//         <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex gap-2">
//           {images.map((_, idx) => (
//             <button
//               key={idx}
//               onClick={(e) => {
//                 e.stopPropagation()
//                 handleDotClick(idx)
//               }}
//               className={`transition-all duration-300 rounded-full ${
//                 idx === currentIndex
//                   ? 'bg-white w-3 h-3'
//                   : 'bg-white/50 hover:bg-white/75 w-2.5 h-2.5'
//               }`}
//               aria-label={`Go to slide ${idx + 1}`}
//               type="button"
//             />
//           ))}
//         </div>
//       )}

//       {/* Slide Counter (Optional) */}
//       {images.length > 1 && (
//         <div className="absolute top-6 right-6 z-30 bg-black/50 text-white px-3 py-1 rounded-full text-sm font-medium">
//           {currentIndex + 1} / {images.length}
//         </div>
//       )}

//       {/* Auto-play indicator (Optional) */}
//       {carouselSettings.autoPlay && images.length > 1 && (
//         <div className="absolute top-6 left-6 z-30 flex items-center gap-2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
//           <div className={`w-2 h-2 rounded-full ${isPaused ? 'bg-yellow-400' : 'bg-green-400'} animate-pulse`} />
//           <span>{isPaused ? 'Paused' : 'Playing'}</span>
//         </div>
//       )}
//     </section>
//   )
// }
















